import { Input, Modal, Select, Message } from "@arco-design/web-react";
import React from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import SubscriptionSelector from "../Components/Subscriptions/Subscriptions";
import { isBlank, isValidEmail, emailFormatError } from "../utils/validators";
import { FetchApi } from "../Api/AuthenticatedApi";
import CustomFieldInputs, { missingRequiredFields, formatErrors } from "../Components/CustomFields/CustomFieldInputs";
import useRecordTypes from "../Components/CustomFields/useRecordTypes";
import ResendSignupEmail from "./ResendSignupEmail";
import useOrgFeatures from "../utils/useOrgFeatures";

const FieldError = styled.div`
  font-size: 12px;
  color: rgb(var(--red-6));
  margin-top: 4px;
`;

// MUI -> Arco. Dialog(+Title/Content/Actions) -> Modal with visible/onOk/
// onCancel/title; the Cancel/Save DialogActions buttons become okText/cancelText.
// TextField -> Input with the MUI label rendered as a small label above each
// field (house style). Arco Input onChange passes the value, not an event.
const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-bottom: 4px;
  margin-top: 8px;
`;

export default function ContactAddEditDialog({ isDataUpdated, allMailListItems, setAllMailListItems, contactSubscriptions, contactName, contactEmail, contactId, contactCompanyId, contactCustomFields, contactRecordTypeId, onSave, isOpen, setIsOpen, render }) {


  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [selectedSubscriptions, setSelectedSubscriptions] = React.useState([]);
  const [disabled, setDisabled] = React.useState(false);
  const [companyId, setCompanyId] = React.useState("");
  const [companies, setCompanies] = React.useState([]);
  const [cfDefs, setCfDefs] = React.useState([]);
  const [customFields, setCustomFields] = React.useState({});
  const [typeId, setTypeId] = React.useState("");
  const { types, defaultTypeId } = useRecordTypes("contact");

  // Seed the type: an existing contact keeps its type; a new one gets the default.
  React.useEffect(() => {
    setTypeId(contactId ? (contactRecordTypeId || "") : (defaultTypeId || ""));
  }, [contactId, contactRecordTypeId, defaultTypeId]);

  const { getAccessTokenSilently } = useAuth0();
  // With companies off the picker is hidden, but the contact keeps its
  // company: companyId still holds it and is saved back unchanged.
  const features = useOrgFeatures();
  React.useEffect(() => {
    if (features.companies) {
      FetchApi(getAccessTokenSilently, "/api/companies/",
        (data) => setCompanies(data || []), () => {}, () => {});
    }
  }, [getAccessTokenSilently, features.companies]);
  React.useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/custom-fields/?entity_type=contact",
      (data) => setCfDefs(data || []), () => {}, () => {});
  }, [getAccessTokenSilently]);


  React.useEffect(() => {
    setName(contactName);
    setEmail(contactEmail);
    setCompanyId(contactCompanyId || "");
    setCustomFields(contactCustomFields || {});
  }, [contactName, contactEmail, contactCompanyId, contactCustomFields])


  React.useEffect(() => {
    // Map contactSubscriptions to the corresponding items in allMailListItems based on id
    const updatedSubscriptions = contactSubscriptions.map(subscription => {
      const matchingMailListItem = allMailListItems.find(item => item.id === subscription.contact_list_id);
      return matchingMailListItem || subscription; // Use the matching item or the original subscription
    });

    setSelectedSubscriptions(updatedSubscriptions);
  }, [contactSubscriptions, allMailListItems]);


  const handleClickOpen = () => {
    setIsOpen(true);
  }

  const handleClose = () => {
    setIsOpen(false);
  };

  const emailErr = emailFormatError(email);
  const isValid = !isBlank(name) && isValidEmail(email);

  const handleSave = () => {
    if (!isValid) {
      return;
    }
    const missing = missingRequiredFields(cfDefs, customFields, typeId);
    if (missing.length) {
      Message.warning("Required: " + missing.join(", "));
      return;
    }
    const badFormat = formatErrors(cfDefs, customFields, typeId);
    if (badFormat.length) {
      Message.warning(badFormat.join("; "));
      return;
    }
    const subscriptions = selectedSubscriptions.map(mailList => ({ contact_list_id: mailList.id }));
    // Only send id when editing. Contact.Id is a non-pointer uuid.UUID on the
    // backend, so an empty-string id on create fails to unmarshal ("invalid UUID
    // length: 0"). Matches how sender-email / contact-list create omit the id.
    // Always send company_id (the selected uuid or null) so an edit doesn't
    // silently unlink; never send "" for a UUID field (Go decode fails on it).
    const payload = { name, email, subscriptions, company_id: companyId || null, record_type_id: typeId || null, custom_fields: customFields };
    if (contactId) {
      payload.id = contactId;
    }
    onSave && onSave(payload);

    handleClose();
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSave();
    }

    return false;
  }


  return (
    <>
      {render(handleClickOpen)}
      <Modal
        visible={isOpen}
        onCancel={handleClose}
        onOk={handleSave}
        title={`${contactId ? "Edit" : "Add"} Contact`}
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{ disabled: !isValid }}
      >
        <FieldLabel>Name</FieldLabel>
        <Input
          autoFocus
          id="name"
          value={name}
          onChange={(value) => {
            setName(value);
          }}
        />

        <FieldLabel>Email</FieldLabel>
        <Input
          id="email"
          value={email}
          error={!!emailErr}
          onChange={(value) => {
            setEmail(value);
          }}
          onKeyDownCapture={handleKeyPress}
        />
        {emailErr && <FieldError>{emailErr}</FieldError>}

        {types.length > 0 && (
          <>
            <FieldLabel>Type</FieldLabel>
            <Select
              allowClear
              placeholder="No type"
              style={{ width: "100%" }}
              value={typeId || undefined}
              onChange={(v) => setTypeId(v || "")}
              options={types.map((t) => ({ label: t.name, value: t.id }))}
            />
          </>
        )}

        {features.companies && (
          <>
            <FieldLabel>Company</FieldLabel>
            <Select
              allowClear
              showSearch
              placeholder="No company"
              style={{ width: "100%" }}
              value={companyId || undefined}
              onChange={(v) => setCompanyId(v || "")}
              options={companies.map((co) => ({ label: co.name, value: co.id }))}
            />
          </>
        )}

        <SubscriptionSelector
          disabled={disabled}
          allContactListItems={allMailListItems}
          setAllMailListItems={setAllMailListItems}
          selectedSubscriptions={selectedSubscriptions}
          setSelectedSubscriptions={setSelectedSubscriptions}
          prompt={"Select Contact Lists"}
        />

        <CustomFieldInputs definitions={cfDefs} values={customFields} typeId={typeId} onChange={setCustomFields} />

        {contactId && <ResendSignupEmail contactId={contactId} />}
      </Modal>
    </>
  );
}
